import { useState, useEffect } from 'react';
import { estoqueService, insumoCategoriasService } from '../../services';
import { formatCurrency } from '../../utils/format';
import './Estoque.css';

const statusColors = { critico: 'badge-danger', baixo: 'badge-warning', atencao: 'badge-info', ok: 'badge-success' };

const validadeColors = { vencido: '#e74c3c', vencendo: '#f39c12', ok: '#27ae60' };

function parseValidadeDate(dateValue) {
  if (!dateValue) return null;

  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), 12, 0, 0);
  }

  const raw = String(dateValue).trim();
  if (!raw) return null;

  const matchDateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (matchDateOnly) {
    const y = Number(matchDateOnly[1]);
    const m = Number(matchDateOnly[2]) - 1;
    const d = Number(matchDateOnly[3]);
    return new Date(y, m, d, 12, 0, 0);
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12, 0, 0);
}

function formatDate(dateValue) {
  const d = parseValidadeDate(dateValue);
  if (!d) return '—';
  return d.toLocaleDateString('pt-BR');
}

function diasParaVencer(dateValue) {
  const val = parseValidadeDate(dateValue);
  if (!val) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return Math.ceil((val - hoje) / 86400000);
}

function toDateInputValue(dateValue) {
  if (!dateValue) return '';
  const raw = String(dateValue).trim();
  const matchDateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (matchDateOnly) return matchDateOnly[1];

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function Estoque() {
  const [insumos, setInsumos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroAlerta, setFiltroAlerta] = useState(null); // 'critico' | 'baixo' | 'ok' | 'vencidos' | 'vencendo'
  const [modal, setModal] = useState(null); // 'novo_insumo' | 'mov' | 'lotes' | 'novo_lote' | 'categorias' | 'ocultos'
  const [insumoAtivo, setInsumoAtivo] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [lotesLoading, setLotesLoading] = useState(false);
  const [erro, setErro] = useState('');

  const [insumoForm, setInsumoForm] = useState({
    nome: '', categoria: '', unidade: 'un', qtd_minima: '', custo_unitario: '',
  });
  const [insumoEditId, setInsumoEditId] = useState(null);
  const [loteForm, setLoteForm] = useState({
    numero_lote: '', data_validade: '', quantidade: '', custo_unitario: '',
  });
  const [loteEditId, setLoteEditId] = useState(null);
  const [movForm, setMovForm] = useState({ insumo_id: '', tipo: 'entrada', quantidade: '', motivo: '' });

  const [categorias, setCategorias] = useState([]);
  const [novaCategoria, setNovaCategoria] = useState('');
  const [categoriaEditId, setCategoriaEditId] = useState(null);
  const [categoriaEditNome, setCategoriaEditNome] = useState('');
  const [erroCategoria, setErroCategoria] = useState('');
  const [categoriaVoltarPara, setCategoriaVoltarPara] = useState(null);

  const [inativos, setInativos] = useState([]);
  const [inativosLoading, setInativosLoading] = useState(false);

  const abrirCategorias = (voltarPara) => {
    setErroCategoria('');
    setCategoriaEditId(null);
    setCategoriaVoltarPara(voltarPara);
    setModal('categorias');
  };

  const carregar = () => {
    estoqueService.listar()
      .then(res => setInsumos(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const carregarCategorias = () => {
    insumoCategoriasService.listar()
      .then(res => setCategorias(res.data))
      .catch(() => {});
  };

  useEffect(() => { carregar(); carregarCategorias(); }, []);

  const abrirOcultos = async () => {
    setModal('ocultos');
    setInativosLoading(true);
    try {
      const res = await estoqueService.listarInativos();
      setInativos(res.data);
    } catch {}
    setInativosLoading(false);
  };

  const ocultarInsumo = async (insumo) => {
    if (!window.confirm(`Ocultar o insumo "${insumo.nome}" da listagem? Ele pode ser reativado depois em "Insumos ocultos".`)) return;
    try {
      await estoqueService.ocultarInsumo(insumo.id);
      carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao ocultar insumo');
    }
  };

  const reativarInsumo = async (insumo) => {
    try {
      await estoqueService.reativarInsumo(insumo.id);
      setInativos(inativos.filter(i => i.id !== insumo.id));
      carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao reativar insumo');
    }
  };

  const salvarNovaCategoria = async (e) => {
    e.preventDefault();
    setErroCategoria('');
    try {
      await insumoCategoriasService.criar({ nome: novaCategoria });
      setNovaCategoria('');
      carregarCategorias();
    } catch (err) {
      setErroCategoria(err.response?.data?.error || 'Erro ao salvar categoria');
    }
  };

  const iniciarEdicaoCategoria = (c) => {
    setErroCategoria('');
    setCategoriaEditId(c.id);
    setCategoriaEditNome(c.nome);
  };

  const salvarEdicaoCategoria = async (e) => {
    e.preventDefault();
    setErroCategoria('');
    try {
      await insumoCategoriasService.atualizar(categoriaEditId, { nome: categoriaEditNome });
      setCategoriaEditId(null);
      setCategoriaEditNome('');
      carregarCategorias();
      carregar();
    } catch (err) {
      setErroCategoria(err.response?.data?.error || 'Erro ao atualizar categoria');
    }
  };

  const removerCategoria = async (c) => {
    setErroCategoria('');
    if (!window.confirm(`Remover a categoria "${c.nome}"?`)) return;
    try {
      await insumoCategoriasService.remover(c.id);
      carregarCategorias();
    } catch (err) {
      setErroCategoria(err.response?.data?.error || 'Erro ao remover categoria');
    }
  };

  const abrirEdicaoInsumo = (insumo) => {
    setErro('');
    setInsumoEditId(insumo.id);
    setInsumoForm({
      nome: insumo.nome,
      categoria: insumo.categoria || '',
      unidade: insumo.unidade,
      qtd_minima: insumo.qtd_minima,
      custo_unitario: insumo.custo_unitario,
    });
    setModal('novo_insumo');
  };

  const abrirLotes = async (insumo) => {
    setInsumoAtivo(insumo);
    setLotesLoading(true);
    setModal('lotes');
    try {
      const res = await estoqueService.listarLotes(insumo.id);
      setLotes(res.data);
    } catch {}
    setLotesLoading(false);
  };

  const salvarInsumo = async (e) => {
    e.preventDefault();
    setErro('');
    try {
      if (insumoEditId) {
        await estoqueService.atualizarInsumo(insumoEditId, insumoForm);
      } else {
        await estoqueService.criarInsumo(insumoForm);
      }
      setModal(null);
      setInsumoEditId(null);
      setInsumoForm({ nome: '', categoria: '', unidade: 'un', qtd_minima: '', custo_unitario: '' });
      carregar();
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao salvar');
    }
  };

  const salvarLote = async (e) => {
    e.preventDefault();
    setErro('');
    try {
      if (loteEditId) {
        await estoqueService.atualizarLote(loteEditId, { ...loteForm, insumo_id: insumoAtivo.id });
      } else {
        await estoqueService.criarLote({ ...loteForm, insumo_id: insumoAtivo.id });
      }
      setLoteForm({ numero_lote: '', data_validade: '', quantidade: '', custo_unitario: '' });
      setLoteEditId(null);
      setModal('lotes');
      const res = await estoqueService.listarLotes(insumoAtivo.id);
      setLotes(res.data);
      carregar();
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao salvar lote');
    }
  };

  const registrarMov = async (e) => {
    e.preventDefault();
    try {
      await estoqueService.movimentacao(movForm);
      setModal(null);
      carregar();
    } catch {}
  };

  const totalVencidos = insumos.reduce((s, i) => s + (i.lotes_vencidos || 0), 0);
  const totalVencendo = insumos.reduce((s, i) => s + (i.lotes_vencendo || 0), 0);

  const buscaLower = busca.trim().toLowerCase();
  const insumosFiltrados = insumos.filter(i => {
    if (buscaLower &&
        !i.nome.toLowerCase().includes(buscaLower) &&
        !(i.categoria || '').toLowerCase().includes(buscaLower)) {
      return false;
    }
    if (filtroAlerta === 'critico' && i.status_estoque !== 'critico') return false;
    if (filtroAlerta === 'baixo' && i.status_estoque !== 'baixo') return false;
    if (filtroAlerta === 'ok' && i.status_estoque !== 'ok') return false;
    if (filtroAlerta === 'vencidos' && !(i.lotes_vencidos > 0)) return false;
    if (filtroAlerta === 'vencendo' && !(i.lotes_vencendo > 0)) return false;
    return true;
  });

  const toggleFiltroAlerta = (valor) => setFiltroAlerta(prev => (prev === valor ? null : valor));

  if (loading) return <div className="loading"><div className="spinner"></div>Carregando...</div>;

  return (
    <div className="estoque-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>📦 Estoque</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => abrirCategorias(null)}>
            ⚙ Categorias
          </button>
          <button className="btn btn-secondary" onClick={abrirOcultos}>
            👁 Insumos ocultos
          </button>
          <button className="btn btn-primary" onClick={() => { setErro(''); setInsumoEditId(null); setInsumoForm({ nome: '', categoria: '', unidade: 'un', qtd_minima: '', custo_unitario: '' }); setModal('novo_insumo'); }}>
            + Novo Insumo
          </button>
        </div>
      </div>

      <div className="estoque-resumo">
        <span
          className={`resumo-item resumo-clicavel${filtroAlerta === 'critico' ? ' resumo-ativo' : ''}`}
          role="button" tabIndex={0}
          onClick={() => toggleFiltroAlerta('critico')}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFiltroAlerta('critico'); } }}
        ><span className="dot" style={{background:'var(--danger)'}}></span> Crítico: {insumos.filter(i => i.status_estoque === 'critico').length}</span>
        <span
          className={`resumo-item resumo-clicavel${filtroAlerta === 'baixo' ? ' resumo-ativo' : ''}`}
          role="button" tabIndex={0}
          onClick={() => toggleFiltroAlerta('baixo')}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFiltroAlerta('baixo'); } }}
        ><span className="dot" style={{background:'var(--warning)'}}></span> Baixo: {insumos.filter(i => i.status_estoque === 'baixo').length}</span>
        <span
          className={`resumo-item resumo-clicavel${filtroAlerta === 'ok' ? ' resumo-ativo' : ''}`}
          role="button" tabIndex={0}
          onClick={() => toggleFiltroAlerta('ok')}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFiltroAlerta('ok'); } }}
        ><span className="dot" style={{background:'var(--success)'}}></span> OK: {insumos.filter(i => i.status_estoque === 'ok').length}</span>
        {totalVencidos > 0 && (
          <span
            className={`resumo-item resumo-clicavel${filtroAlerta === 'vencidos' ? ' resumo-ativo' : ''}`}
            role="button" tabIndex={0}
            style={{color:'#e74c3c', fontWeight:700}}
            onClick={() => toggleFiltroAlerta('vencidos')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFiltroAlerta('vencidos'); } }}
          >⚠ {totalVencidos} lote{totalVencidos > 1 ? 's' : ''} vencido{totalVencidos > 1 ? 's' : ''}</span>
        )}
        {totalVencendo > 0 && (
          <span
            className={`resumo-item resumo-clicavel${filtroAlerta === 'vencendo' ? ' resumo-ativo' : ''}`}
            role="button" tabIndex={0}
            style={{color:'#f39c12', fontWeight:700}}
            onClick={() => toggleFiltroAlerta('vencendo')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFiltroAlerta('vencendo'); } }}
          >🕐 {totalVencendo} vencendo em 30 dias</span>
        )}
        {filtroAlerta && (
          <button className="btn btn-sm btn-outline" onClick={() => setFiltroAlerta(null)}>✕ Limpar filtro</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="🔍 Buscar insumo por nome ou categoria..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        {busca && (
          <button className="btn btn-outline" onClick={() => setBusca('')}>Limpar</button>
        )}
      </div>

      <div className="table-container card">
        <table>
          <thead>
            <tr>
              <th>Insumo</th>
              <th>Estoque</th>
              <th>Mínimo</th>
              <th>Custo Unit.</th>
              <th>Valor Total</th>
              <th>Status</th>
              <th>Lotes</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {insumosFiltrados.map(i => (
              <tr key={i.id}>
                <td><strong>{i.nome}</strong><br/><small>{i.categoria}</small></td>
                <td>{i.qtd_estoque} {i.unidade}</td>
                <td>{i.qtd_minima} {i.unidade}</td>
                <td>{formatCurrency(i.custo_unitario)}</td>
                <td>{formatCurrency(i.valor_total)}</td>
                <td><span className={`badge ${statusColors[i.status_estoque]}`}>{i.status_estoque}</span></td>
                <td>
                  <button
                    className="btn btn-sm"
                    style={{ background: i.lotes_vencidos > 0 ? '#ffeaea' : i.lotes_vencendo > 0 ? '#fff8e6' : '#f0f7ff', border: '1px solid', borderColor: i.lotes_vencidos > 0 ? '#e74c3c' : i.lotes_vencendo > 0 ? '#f39c12' : '#b3d4f5', color: i.lotes_vencidos > 0 ? '#e74c3c' : i.lotes_vencendo > 0 ? '#c07000' : '#1a6bb5' }}
                    onClick={() => abrirLotes(i)}
                  >
                    {i.lotes_vencidos > 0 ? '⚠ ' : i.lotes_vencendo > 0 ? '🕐 ' : '📋 '}
                    {i.lotes_ativos + i.lotes_vencidos} lote{(i.lotes_ativos + i.lotes_vencidos) !== 1 ? 's' : ''}
                  </button>
                </td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-primary" onClick={() => { setMovForm({insumo_id: i.id, tipo:'entrada', quantidade:'', motivo:''}); setModal('mov'); }}>
                    📥 Movimentar
                  </button>
                  <button className="btn-icon" title="Editar insumo" onClick={() => abrirEdicaoInsumo(i)}>✏️</button>
                  <button className="btn-icon" title="Ocultar insumo" onClick={() => ocultarInsumo(i)}>🗑️</button>
                </td>
              </tr>
            ))}
            {insumosFiltrados.length === 0 && (
              <tr><td colSpan="8" className="no-data">
                {busca || filtroAlerta ? 'Nenhum insumo encontrado' : 'Nenhum insumo cadastrado'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Novo Insumo */}
      {modal === 'novo_insumo' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{insumoEditId ? 'Editar Insumo' : 'Novo Insumo'}</h2>
            {erro && <div className="alert alert-danger">{erro}</div>}
            <form onSubmit={salvarInsumo}>
              <div className="form-group">
                <label>Nome *</label>
                <input value={insumoForm.nome} onChange={e => setInsumoForm({...insumoForm, nome: e.target.value})} required />
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Categoria</label>
                  <button type="button" className="btn-link" onClick={() => abrirCategorias('novo_insumo')}>
                    ⚙ Gerenciar categorias
                  </button>
                </div>
                <select value={insumoForm.categoria} onChange={e => setInsumoForm({...insumoForm, categoria: e.target.value})}>
                  <option value="">Selecione uma categoria...</option>
                  {categorias.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Unidade *</label>
                  <select value={insumoForm.unidade} onChange={e => setInsumoForm({...insumoForm, unidade: e.target.value})}>
                    <option value="un">un (unidade)</option>
                    <option value="kg">kg</option>
                    <option value="g">g (gramas)</option>
                    <option value="L">L (litros)</option>
                    <option value="ml">ml</option>
                    <option value="cx">cx (caixa)</option>
                    <option value="pct">pct (pacote)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Estoque mínimo</label>
                  <input type="number" step="0.01" value={insumoForm.qtd_minima} onChange={e => setInsumoForm({...insumoForm, qtd_minima: e.target.value})} placeholder="0" />
                </div>
              </div>
              <div className="form-group">
                <label>Custo unitário (R$){insumoEditId && <small style={{fontWeight:400, color:'#999'}}> — calculado automaticamente pelos lotes</small>}</label>
                {insumoEditId ? (
                  <input type="text" value={formatCurrency(insumoForm.custo_unitario)} disabled />
                ) : (
                  <input type="number" step="0.0001" value={insumoForm.custo_unitario} onChange={e => setInsumoForm({...insumoForm, custo_unitario: e.target.value})} placeholder="0.00" />
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gerenciar Categorias */}
      {modal === 'categorias' && (
        <div className="modal-overlay" onClick={() => setModal(categoriaVoltarPara)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Gerenciar Categorias</h2>
            {erroCategoria && <div className="alert alert-danger">{erroCategoria}</div>}

            <form onSubmit={salvarNovaCategoria} className="cat-nova-form">
              <input
                value={novaCategoria}
                onChange={e => setNovaCategoria(e.target.value)}
                placeholder="Nova categoria (ex: Bebidas)"
                required
              />
              <button type="submit" className="btn btn-primary btn-sm">+ Adicionar</button>
            </form>

            <div className="cat-lista">
              {categorias.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>
                  Nenhuma categoria cadastrada.
                </div>
              ) : categorias.map(c => (
                <div key={c.id} className="cat-item">
                  {categoriaEditId === c.id ? (
                    <form onSubmit={salvarEdicaoCategoria} className="cat-edit-form">
                      <input value={categoriaEditNome} onChange={e => setCategoriaEditNome(e.target.value)} required autoFocus />
                      <button type="submit" className="btn btn-sm btn-primary">Salvar</button>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => setCategoriaEditId(null)}>Cancelar</button>
                    </form>
                  ) : (
                    <>
                      <span className="cat-nome">{c.nome} <small>({c.total_insumos} insumo{c.total_insumos !== 1 ? 's' : ''})</small></span>
                      <div className="cat-acoes">
                        <button type="button" className="btn-icon" title="Editar" onClick={() => iniciarEdicaoCategoria(c)}>✏️</button>
                        <button type="button" className="btn-icon" title="Remover" onClick={() => removerCategoria(c)}>🗑️</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setModal(categoriaVoltarPara)}>
                {categoriaVoltarPara ? 'Voltar' : 'Fechar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Insumos Ocultos */}
      {modal === 'ocultos' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Insumos Ocultos</h2>
            {inativosLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>Carregando...</div>
            ) : inativos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>
                Nenhum insumo oculto.<br/>
                <small>Insumos com estoque zerado e sem uso em fichas técnicas ou produtos de revenda podem ser ocultados na listagem principal.</small>
              </div>
            ) : (
              <div className="cat-lista">
                {inativos.map(i => (
                  <div key={i.id} className="cat-item">
                    <span className="cat-nome">{i.nome} <small>{i.categoria}</small></span>
                    <div className="cat-acoes">
                      <button type="button" className="btn btn-sm btn-primary" onClick={() => reativarInsumo(i)}>Reativar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Lotes */}
      {modal === 'lotes' && insumoAtivo && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" style={{ maxWidth: 920, width: '95vw' }} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
              <div>
                <h2 style={{margin:0}}>{insumoAtivo.nome}</h2>
                <small style={{color:'#888'}}>Estoque atual: {insumoAtivo.qtd_estoque} {insumoAtivo.unidade}</small>
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setErro('');
                  setLoteEditId(null);
                  setLoteForm({ numero_lote: '', data_validade: '', quantidade: '', custo_unitario: '' });
                  setModal('novo_lote');
                }}
              >
                + Novo Lote
              </button>
            </div>

            {lotesLoading ? (
              <div style={{textAlign:'center', padding:24, color:'#888'}}>Carregando...</div>
            ) : lotes.length === 0 ? (
              <div style={{textAlign:'center', padding:24, color:'#aaa'}}>
                Nenhum lote cadastrado.<br/>
                <small>Clique em "+ Novo Lote" para registrar uma entrada com lote e validade.</small>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{width:'100%', minWidth: 760, borderCollapse:'collapse', fontSize:14}}>
                <thead>
                  <tr style={{borderBottom:'2px solid #eee'}}>
                    <th style={{textAlign:'left', padding:'6px 8px'}}>Lote</th>
                    <th style={{textAlign:'left', padding:'6px 8px'}}>Validade</th>
                    <th style={{textAlign:'right', padding:'6px 8px'}}>Qtd</th>
                    <th style={{textAlign:'right', padding:'6px 8px'}}>Custo</th>
                    <th style={{textAlign:'center', padding:'6px 8px'}}>Status</th>
                    <th style={{textAlign:'center', padding:'6px 8px'}}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {lotes.map(l => {
                    const dias = diasParaVencer(l.data_validade);
                    const cor = validadeColors[l.status_validade] || '#888';
                    return (
                      <tr key={l.id} style={{borderBottom:'1px solid #f5f5f5'}}>
                        <td style={{padding:'8px 8px'}}>{l.numero_lote || <span style={{color:'#ccc'}}>—</span>}</td>
                        <td style={{padding:'8px 8px', color: cor, fontWeight: l.status_validade !== 'ok' ? 700 : 400}}>
                          {formatDate(l.data_validade)}
                          {dias !== null && dias <= 30 && (
                            <div style={{fontSize:11}}>
                              {dias < 0 ? `Venceu há ${Math.abs(dias)} dias` : dias === 0 ? 'Vence hoje!' : `${dias} dias`}
                            </div>
                          )}
                        </td>
                        <td style={{padding:'8px 8px', textAlign:'right'}}>{l.quantidade} {insumoAtivo.unidade}</td>
                        <td style={{padding:'8px 8px', textAlign:'right'}}>{l.custo_unitario > 0 ? formatCurrency(l.custo_unitario) : '—'}</td>
                        <td style={{padding:'8px 8px', textAlign:'center'}}>
                          {l.status_validade === 'vencido' && <span style={{background:'#ffeaea', color:'#e74c3c', padding:'2px 8px', borderRadius:20, fontSize:12, fontWeight:700}}>Vencido</span>}
                          {l.status_validade === 'vencendo' && <span style={{background:'#fff8e6', color:'#c07000', padding:'2px 8px', borderRadius:20, fontSize:12, fontWeight:700}}>Vencendo</span>}
                          {l.status_validade === 'ok' && <span style={{background:'#eafaf1', color:'#27ae60', padding:'2px 8px', borderRadius:20, fontSize:12, fontWeight:700}}>OK</span>}
                        </td>
                        <td style={{padding:'8px 8px', textAlign:'center'}}>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => {
                              setErro('');
                              setLoteEditId(l.id);
                              setLoteForm({
                                numero_lote: l.numero_lote || '',
                                data_validade: toDateInputValue(l.data_validade),
                                quantidade: l.quantidade ?? '',
                                custo_unitario: l.custo_unitario ?? '',
                              });
                              setModal('novo_lote');
                            }}
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
            )}

            <div className="modal-actions" style={{marginTop:16}}>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Novo Lote */}
      {modal === 'novo_lote' && insumoAtivo && (
        <div className="modal-overlay" onClick={() => setModal('lotes')}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{loteEditId ? 'Editar Lote' : 'Novo Lote'} — {insumoAtivo.nome}</h2>
            {erro && <div className="alert alert-danger">{erro}</div>}
            <form onSubmit={salvarLote}>
              <div className="form-group">
                <label>Número do lote</label>
                <input value={loteForm.numero_lote} onChange={e => setLoteForm({...loteForm, numero_lote: e.target.value})} placeholder="Ex: L2026-001" />
              </div>
              <div className="form-group">
                <label>Data de validade</label>
                <input type="date" value={loteForm.data_validade} onChange={e => setLoteForm({...loteForm, data_validade: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Quantidade ({insumoAtivo.unidade}) *</label>
                  <input type="number" step="0.01" value={loteForm.quantidade} onChange={e => setLoteForm({...loteForm, quantidade: e.target.value})} required placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Custo unitário (R$)</label>
                  <input type="number" step="0.0001" value={loteForm.custo_unitario} onChange={e => setLoteForm({...loteForm, custo_unitario: e.target.value})} placeholder="0.0000" />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setLoteEditId(null); setLoteForm({ numero_lote: '', data_validade: '', quantidade: '', custo_unitario: '' }); setModal('lotes'); }}>Voltar</button>
                <button type="submit" className="btn btn-primary">{loteEditId ? 'Salvar alterações' : 'Registrar Lote'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Movimentação */}
      {modal === 'mov' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Movimentação de Estoque</h2>
            <form onSubmit={registrarMov}>
              <div className="form-group">
                <label>Tipo</label>
                <select value={movForm.tipo} onChange={e => setMovForm({...movForm, tipo: e.target.value})}>
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                  <option value="ajuste">Ajuste</option>
                </select>
              </div>
              <div className="form-group">
                <label>Quantidade</label>
                <input type="number" step="0.01" value={movForm.quantidade} onChange={e => setMovForm({...movForm, quantidade: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Motivo</label>
                <input value={movForm.motivo} onChange={e => setMovForm({...movForm, motivo: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Registrar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
